import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";

export default function Footer({ isDarkMode }) {
  // Colors object با useMemo
  const colors = useMemo(
    () => ({
      bg: isDarkMode ? "#1a1a1a" : "#ffffff",
      border: isDarkMode ? "#333" : "#e0e0e0",
      text: isDarkMode ? "#b0b0b0" : "#666",
      textLight: isDarkMode ? "#888" : "#999",
      heading: isDarkMode ? "#ffffff" : "#000000",
      shadowBox: isDarkMode ? "0 -2px 8px rgba(0,0,0,0.3)" : "0 -2px 8px rgba(0,0,0,0.1)",
      hoverBg: isDarkMode ? "#2a2a2a" : "#f5f5f5"
    }),
    [isDarkMode]
  );

  const footerStyle = useMemo(
    () => ({
      backgroundColor: colors.bg,
      borderTop: `1px solid ${colors.border}`,
      padding: "clamp(15px, 3vw, 30px) clamp(12px, 5vw, 40px)",
      color: colors.text,
      marginTop: "0px",
      boxShadow: colors.shadowBox,
      transition: "all 0.3s ease"
    }),
    [colors]
  );

  const containerStyle = useMemo(
    () => ({
      maxWidth: "1200px",
      margin: "0 auto",
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(clamp(140px, 50vw, 200px), 1fr))",
      gap: "clamp(20px, 5vw, 40px)"
    }),
    []
  );

  const columnStyle = useMemo(
    () => ({
      display: "flex",
      flexDirection: "column",
      gap: "clamp(10px, 2vw, 15px)"
    }),
    []
  );

  const headingStyle = useMemo(
    () => ({
      fontSize: "clamp(13px, 2.5vw, 16px)",
      fontWeight: "bold",
      color: colors.heading,
      marginBottom: "clamp(5px, 1vw, 10px)"
    }),
    [colors.heading]
  );

  const linkStyle = useCallback(
    (e) => ({
      color: e ? colors.text : colors.text,
      textDecoration: "none",
      cursor: "pointer",
      fontSize: "clamp(12px, 2vw, 14px)",
      transition: "all 0.3s ease",
      display: "block",
      padding: "clamp(4px, 1vw, 8px) 0"
    }),
    [colors.text]
  );

  const bottomStyle = useMemo(
    () => ({
      borderTop: `1px solid ${colors.border}`,
      paddingTop: "clamp(15px, 3vw, 30px)",
      marginTop: "clamp(15px, 3vw, 30px)",
      textAlign: "center",
      color: colors.textLight,
      fontSize: "clamp(12px, 2vw, 14px)"
    }),
    [colors]
  );

  // Mobile: single column، Desktop: grid
  const mobileContainerStyle = useMemo(
    () => ({
      ...containerStyle,
      gridTemplateColumns: "1fr",
      gap: "clamp(20px, 4vw, 30px)"
    }),
    [containerStyle]
  );

  return (
    <footer style={footerStyle}>
      {/* Desktop: 4 columns, Mobile: 1 column */}
      <div style={containerStyle}>
        {/* Logo Section */}
        <div style={columnStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "clamp(8px, 2vw, 10px)" }}>
            <img
              src="/images/logo.png"
              alt="ClipTranscript"
              style={{
                height: "clamp(24px, 5vw, 32px)",
                width: "auto",
                transition: "transform 0.3s ease"
              }}
              onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
              onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
            />
            <h3 style={{ ...headingStyle, marginBottom: "0", fontSize: "clamp(14px, 3vw, 18px)" }}>
              ClipTranscript
            </h3>
          </div>
          <p
            style={{
              fontSize: "clamp(12px, 2vw, 14px)",
              lineHeight: "1.6",
              color: colors.text,
              margin: 0
            }}
          >
            Turn any YouTube video into text. Fast, accurate. No ads. No sign-up required.
          </p>
        </div>

        {/* Product Section */}
        <div style={columnStyle}>
          <h4 style={headingStyle}>Product</h4>
          <a
            href="#features"
            style={linkStyle()}
            onMouseEnter={(e) => (e.target.style.color = "#007bff")}
            onMouseLeave={(e) => (e.target.style.color = colors.text)}
          >
            Features
          </a>
          <a
            href="#how-it-works"
            style={linkStyle()}
            onMouseEnter={(e) => (e.target.style.color = "#007bff")}
            onMouseLeave={(e) => (e.target.style.color = colors.text)}
          >
            How it Works
          </a>
          <a
            href="#pricing"
            style={linkStyle()}
            onMouseEnter={(e) => (e.target.style.color = "#007bff")}
            onMouseLeave={(e) => (e.target.style.color = colors.text)}
          >
            Pricing
          </a>
        </div>

        {/* Legal Section */}
        <div style={columnStyle}>
          <h4 style={headingStyle}>Legal</h4>
          <Link
            to="/terms"
            style={linkStyle()}
            onMouseEnter={(e) => (e.target.style.color = "#007bff")}
            onMouseLeave={(e) => (e.target.style.color = colors.text)}
          >
            Terms &amp; Conditions
          </Link>
          <a
            href="#contact"
            style={linkStyle()}
            onMouseEnter={(e) => (e.target.style.color = "#007bff")}
            onMouseLeave={(e) => (e.target.style.color = colors.text)}
          >
            Contact Us
          </a>
        </div>

        {/* Support Section */}
        <div style={columnStyle}>
          <h4 style={headingStyle}>Support</h4>
          <a
            href="#faq"
            style={linkStyle()}
            onMouseEnter={(e) => (e.target.style.color = "#007bff")}
            onMouseLeave={(e) => (e.target.style.color = colors.text)}
          >
            FAQ
          </a>
          <a
            href="#help"
            style={linkStyle()}
            onMouseEnter={(e) => (e.target.style.color = "#007bff")}
            onMouseLeave={(e) => (e.target.style.color = colors.text)}
          >
            Help Center
          </a>
          <a
            href="#blog"
            style={linkStyle()}
            onMouseEnter={(e) => (e.target.style.color = "#007bff")}
            onMouseLeave={(e) => (e.target.style.color = colors.text)}
          >
            Blog
          </a>
        </div>
      </div>

      {/* Bottom Copyright */}
      <div style={bottomStyle}>
        <p style={{ margin: 0 }}>© 2026 ClipTranscript. All rights reserved.</p>
      </div>

      {/* Mobile Styles */}
      <style>{`
        @media (max-width: 768px) {
          footer {
            padding: clamp(12px, 4vw, 20px) clamp(12px, 4vw, 20px) !important;
          }
        }

        @media (max-width: 480px) {
          [data-footer-container] {
            grid-template-columns: 1fr !important;
            gap: clamp(15px, 3vw, 20px) !important;
          }
        }
      `}</style>
    </footer>
  );
}
